import { useLayoutEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api.ts';
import type { JiraTicketDTO } from '../lib/api.ts';
import type { JiraTooltipTarget } from '../features/jira/RepoJiraTickets.tsx';

// Palette fixe pour le fallback (avatarUrl null selon le réglage de confidentialité du compte
// Atlassian, voir JiraTicketDTO.assignee côté shared) — dans le cas courant, avatarUrl pointe déjà
// vers l'image générée par Jira (initiales sur fond coloré), donc <img> seul reproduit déjà le
// look Jira sans effort supplémentaire.
const AVATAR_FALLBACK_COLORS = ['#0052CC', '#00875A', '#DE350B', '#6554C0', '#FF8B00', '#008DA6'];

function initials(name: string): string {
  const parts = name.trim().split(/\s+/);
  return ((parts[0]?.[0] ?? '') + (parts.length > 1 ? parts[parts.length - 1][0] : '')).toUpperCase();
}

function fallbackColor(name: string): string {
  let hash = 0;
  // Math.imul fait le wraparound 32 bits signé lui-même (pas de `| 0` séparé nécessaire) — hash
  // classique à la String.hashCode() de Java, juste pour répartir les couleurs de fallback.
  for (let i = 0; i < name.length; i++) hash = Math.imul(hash, 31) + name.charCodeAt(i);
  return AVATAR_FALLBACK_COLORS[Math.abs(hash) % AVATAR_FALLBACK_COLORS.length];
}

function JiraAvatar({ name, avatarUrl }: { name: string; avatarUrl: string | null }) {
  if (avatarUrl) return <img className="jira-avatar" src={avatarUrl} alt="" />;
  return (
    <span className="jira-avatar jira-avatar-fallback" style={{ background: fallbackColor(name) }}>
      {initials(name)}
    </span>
  );
}

interface AssigneeFieldProps {
  ticketKey: string;
  canAssign: boolean;
  assignee: JiraTicketDTO['assignee'];
  onMutated: () => void;
}

// Extrait de JiraTicketTooltip (S3776 : complexité cognitive) — porte son propre état d'édition et
// sa propre query d'utilisateurs assignables, indépendants du champ Statut.
function JiraAssigneeField({ ticketKey, canAssign, assignee, onMutated }: AssigneeFieldProps) {
  const [editing, setEditing] = useState(false);
  const [query, setQuery] = useState('');

  // Liste d'utilisateurs assignables — seulement une fois l'édition ouverte ET au moins un
  // caractère tapé (findAssignableUsers exige `query` en l'absence d'un accountId/username exact,
  // voir getAssignableUsers côté service) : évite un appel Jira par simple survol de la pastille.
  const assignableUsersQuery = useQuery({
    queryKey: ['jira-assignable-users', ticketKey, query],
    queryFn: () => api.assignableUsers(ticketKey, query),
    enabled: editing && query.length > 0,
    staleTime: 10_000,
  });

  const mutation = useMutation({
    mutationFn: (accountId: string | null) => api.updateAssignee({ ticketKey, accountId }),
    onSuccess: () => {
      onMutated();
      setEditing(false);
      setQuery('');
    },
  });

  if (!canAssign) {
    return (
      // Toujours affiché, même sans assignee — comme dans Jira où le champ Assignee
      // reste visible avec "Unassigned" plutôt que de disparaître.
      <span className="jira-tooltip-assignee">
        {assignee && <JiraAvatar name={assignee.displayName} avatarUrl={assignee.avatarUrl} />}
        {assignee?.displayName ?? 'Non assigné'}
      </span>
    );
  }

  if (!editing) {
    return (
      <button type="button" className="jira-tooltip-editable jira-tooltip-assignee" onClick={() => setEditing(true)}>
        {assignee && <JiraAvatar name={assignee.displayName} avatarUrl={assignee.avatarUrl} />}
        {assignee?.displayName ?? 'Non assigné'}
      </button>
    );
  }

  return (
    <div className="jira-tooltip-editor">
      <input
        autoFocus
        className="jira-tooltip-editor-input"
        placeholder="Rechercher un utilisateur…"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
      />
      <div className="jira-tooltip-editor-list">
        <button type="button" disabled={mutation.isPending} onClick={() => mutation.mutate(null)}>
          Non assigné
        </button>
        {query.length === 0 ? (
          <span className="muted" style={{ fontSize: 12, padding: '3px 6px' }}>Tapez pour rechercher…</span>
        ) : assignableUsersQuery.isLoading ? (
          <span className="muted" style={{ fontSize: 12, padding: '3px 6px' }}>Recherche…</span>
        ) : assignableUsersQuery.data?.length === 0 ? (
          <span className="muted" style={{ fontSize: 12, padding: '3px 6px' }}>Aucun résultat</span>
        ) : (
          assignableUsersQuery.data?.map((u) => (
            <button
              key={u.accountId}
              type="button"
              disabled={mutation.isPending}
              onClick={() => mutation.mutate(u.accountId)}
            >
              <JiraAvatar name={u.displayName} avatarUrl={u.avatarUrl} />
              {u.displayName}
            </button>
          ))
        )}
      </div>
      {mutation.isError && <span className="jira-tooltip-editor-error">Échec de la mise à jour</span>}
      <button
        type="button"
        className="jira-tooltip-editor-cancel"
        onClick={() => { setEditing(false); setQuery(''); }}
      >
        ✕
      </button>
    </div>
  );
}

interface StatusFieldProps {
  ticketKey: string;
  status: string;
  onMutated: () => void;
}

// Extrait de JiraTicketTooltip (S3776) — porte transitionsQuery/transitionMutation, indépendants
// du champ Assignee.
function JiraStatusField({ ticketKey, status, onMutated }: StatusFieldProps) {
  const [editing, setEditing] = useState(false);
  const queryClient = useQueryClient();

  // Transitions exécutables — chargées dès le montage (pas seulement au clic sur le statut) :
  // contrairement à `canAssign` (déjà porté par JiraTicketDTO), Jira n'expose aucun booléen de
  // droit pour les transitions, seule la liste elle-même (vide ou non) le révèle. Sans ce fetch
  // anticipé, impossible de savoir s'il faut afficher le statut en lecture seule ou éditable avant
  // que l'utilisateur clique dessus.
  const transitionsQuery = useQuery({
    queryKey: ['jira-ticket-transitions', ticketKey],
    queryFn: () => api.ticketTransitions(ticketKey),
    staleTime: 15_000,
  });
  const canTransition = (transitionsQuery.data?.length ?? 0) > 0;

  const mutation = useMutation({
    mutationFn: (transitionId: string) => api.transitionTicket(ticketKey, transitionId),
    onSuccess: () => {
      onMutated();
      queryClient.invalidateQueries({ queryKey: ['jira-ticket-transitions', ticketKey] });
      setEditing(false);
    },
  });

  if (!canTransition) return <span className="badge">{status}</span>;

  if (!editing) {
    return (
      <button type="button" className="badge jira-tooltip-editable-badge" onClick={() => setEditing(true)}>
        {status}
      </button>
    );
  }

  return (
    <div className="jira-tooltip-editor">
      <div className="jira-tooltip-editor-list">
        {transitionsQuery.data?.map((t) => (
          <button
            key={t.id}
            type="button"
            disabled={mutation.isPending}
            onClick={() => mutation.mutate(t.id)}
          >
            {t.name}
          </button>
        ))}
      </div>
      {mutation.isError && <span className="jira-tooltip-editor-error">Échec de la transition</span>}
      <button type="button" className="jira-tooltip-editor-cancel" onClick={() => setEditing(false)}>✕</button>
    </div>
  );
}

interface FixVersionsFieldProps {
  ticketKey: string;
  /** Nom du repo affiché : seules les versions dont le nom le contient sont proposées. */
  repoName: string | undefined;
  canEdit: boolean;
  fixVersions: JiraTicketDTO['fixVersions'];
  onMutated: () => void;
}

function fixVersionsLabel(fixVersions: JiraTicketDTO['fixVersions']): string {
  return fixVersions.length > 0 ? fixVersions.map((v) => v.name).join(', ') : 'Aucune';
}

// Multi-select : chaque case cochée/décochée déclenche immédiatement sa propre mutation (même
// principe qu'Assignee/Statut : un appel Jira par action), avec la liste COMPLÈTE résultante
// (sémantique "set" côté Jira). L'éditeur reste ouvert entre deux bascules, et les cases sont
// désactivées pendant la mutation pour éviter deux écritures concurrentes sur un état périmé.
function JiraFixVersionsField({ ticketKey, repoName, canEdit, fixVersions, onMutated }: FixVersionsFieldProps) {
  const [editing, setEditing] = useState(false);

  const versionsQuery = useQuery({
    queryKey: ['jira-unreleased-versions', ticketKey],
    queryFn: () => api.unreleasedVersions(ticketKey),
    enabled: editing,
    staleTime: 60_000,
  });

  const mutation = useMutation({
    mutationFn: (versionIds: string[]) => api.updateFixVersions({ ticketKey, versionIds }),
    onSuccess: onMutated,
  });

  if (!canEdit) return <span className="badge">{fixVersionsLabel(fixVersions)}</span>;

  if (!editing) {
    return (
      <button type="button" className="badge jira-tooltip-editable-badge" onClick={() => setEditing(true)}>
        {fixVersionsLabel(fixVersions)}
      </button>
    );
  }

  const selectedIds = new Set(fixVersions.map((v) => v.id));
  const toggle = (id: string) => {
    const next = new Set(selectedIds);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    mutation.mutate([...next]);
  };
  // Versions déjà posées mais absentes de la liste "non publiées" (ex. version déjà publiée) :
  // affichées cochées quand même, sinon l'utilisateur ne pourrait pas les retirer.
  // Filtre insensible à la casse sur le nom du repo — pas de filtre tant que le nom n'est pas
  // résolu (repoMetaQuery en cours), pour ne pas afficher une liste vide à tort.
  const needle = repoName?.toLowerCase();
  const listed = (versionsQuery.data ?? []).filter((v) => !needle || v.name.toLowerCase().includes(needle));
  const extra = fixVersions.filter((v) => !listed.some((l) => l.id === v.id));

  return (
    <div className="jira-tooltip-editor">
      <div className="jira-tooltip-editor-list">
        {versionsQuery.isLoading && <span className="muted" style={{ fontSize: 12, padding: '3px 6px' }}>Chargement…</span>}
        {versionsQuery.isSuccess && listed.length === 0 && extra.length === 0 && (
          <span className="muted" style={{ fontSize: 12, padding: '3px 6px' }}>Aucune version non publiée</span>
        )}
        {[...extra, ...listed].map((v) => (
          <label key={v.id} className="jira-tooltip-editor-check">
            <input
              type="checkbox"
              checked={selectedIds.has(v.id)}
              disabled={mutation.isPending}
              onChange={() => toggle(v.id)}
            />
            {v.name}
          </label>
        ))}
      </div>
      {(mutation.isError || versionsQuery.isError) && (
        <span className="jira-tooltip-editor-error">Échec de la mise à jour</span>
      )}
      <button type="button" className="jira-tooltip-editor-cancel" onClick={() => setEditing(false)}>✕</button>
    </div>
  );
}

interface Props {
  ticket: JiraTooltipTarget;
  /** Bord droit de la sidebar (viewport coords) — la tooltip s'accroche juste à droite, pas au curseur. */
  anchorX: number;
  /** Bornes verticales (viewport coords) de la ligne survolée, pour centrer la tooltip dessus. */
  rowTop: number;
  rowBottom: number;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
  /** Nom du repo courant (Repository.name) — filtre la liste des fix versions proposées. */
  repoName?: string;
  /** Pan/zoom le graphe jusqu'au label de cette branche (RepoViewPage.tsx : setFocusedBranchName,
      même mécanisme que le clic sur une branche dans BranchFilter.tsx). */
  onFlyToBranch: (branchName: string) => void;
}

const SIDEBAR_GAP = 5;
const VIEWPORT_MARGIN = 8;
// Même géométrie de pointe que MrTooltip/CherryPickTooltip (voir leurs commentaires) — rayon 6px
// + moitié hauteur pointe (8px), pour qu'elle ne chevauche jamais un coin arrondi.
const ARROW_MARGIN = 14;

// Positionnement identique à MrTooltip.tsx/CherryPickTooltip.tsx (même ancrage sur le bord de la
// sidebar, même pointe, même ResizeObserver pour suivre le contenu qui grandit une fois
// detailQuery résolue) — dupliqué plutôt que factorisé, même raison que CherryPickTooltip : la
// mécanique de positionnement est tout ce que ces tooltips ont en commun.
export function JiraTicketTooltip({ ticket, anchorX, rowTop, rowBottom, onMouseEnter, onMouseLeave, repoName, onFlyToBranch }: Props) {
  const tooltipRef = useRef<HTMLDivElement>(null);
  const queryClient = useQueryClient();
  const [top, setTop] = useState(() => Math.max(VIEWPORT_MARGIN, rowTop));
  const [arrowTop, setArrowTop] = useState(() => (rowTop + rowBottom) / 2 - Math.max(VIEWPORT_MARGIN, rowTop));

  useLayoutEffect(() => {
    const el = tooltipRef.current;
    if (!el) return undefined;
    const reposition = () => {
      const height = el.offsetHeight;
      const rowCenterY = (rowTop + rowBottom) / 2;
      const ideal = rowCenterY - height / 2;
      const clampedTop = Math.max(
        VIEWPORT_MARGIN,
        Math.min(ideal, window.innerHeight - height - VIEWPORT_MARGIN),
      );
      setTop(clampedTop);
      setArrowTop(Math.max(ARROW_MARGIN, Math.min(rowCenterY - clampedTop, height - ARROW_MARGIN)));
    };
    reposition();
    const ro = new ResizeObserver(reposition);
    ro.observe(el);
    return () => ro.disconnect();
  }, [rowTop, rowBottom]);

  // Détail (statut, assignee) récupéré à la demande au survol, même principe que le detailQuery
  // de MrTooltip : la liste elle-même (RepoJiraTickets.tsx) n'a que la clé/titre/branches, un
  // getIssue complet par ticket coûterait cher pour des tickets jamais survolés.
  const detailQuery = useQuery({
    queryKey: ['jira-ticket-detail', ticket.ticketKey],
    queryFn: () => api.ticketDetail(ticket.ticketKey),
    staleTime: 15_000,
  });

  const invalidateAfterEdit = () => {
    queryClient.invalidateQueries({ queryKey: ['jira-ticket-detail', ticket.ticketKey] });
    // Rafraîchit aussi la liste de la sidebar (RepoJiraTickets.tsx) : sa pastille `isDone` vient
    // d'un fetch séparé au chargement de la liste (resolveRepoTicketSummaries), donc une
    // transition faite ici ne la mettrait sinon à jour qu'au prochain rechargement de repo.
    queryClient.invalidateQueries({ queryKey: ['jira-repo-tickets'] });
  };

  return (
    <div
      ref={tooltipRef}
      className="jira-tooltip"
      style={{ left: anchorX + SIDEBAR_GAP, top, '--arrow-top': `${arrowTop}px` } as React.CSSProperties}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <div className="mr-tooltip-header">
        {ticket.url ? (
          <a className="mr-tooltip-iid" href={ticket.url} target="_blank" rel="noreferrer">{ticket.ticketKey}</a>
        ) : (
          <code className="mr-tooltip-iid">{ticket.ticketKey}</code>
        )}
        <span className="mr-tooltip-title">{detailQuery.data?.summary ?? ticket.summary}</span>
      </div>
      {detailQuery.data && (
        <div className="mr-tooltip-pipeline">
          <span className="mr-tooltip-pipeline-label jira-tooltip-field-label">Assignee</span>
          <JiraAssigneeField
            ticketKey={ticket.ticketKey}
            canAssign={detailQuery.data.canAssign}
            assignee={detailQuery.data.assignee}
            onMutated={invalidateAfterEdit}
          />
        </div>
      )}
      {detailQuery.data?.status && (
        <div className="mr-tooltip-pipeline">
          <span className="mr-tooltip-pipeline-label jira-tooltip-field-label">Statut</span>
          <JiraStatusField ticketKey={ticket.ticketKey} status={detailQuery.data.status} onMutated={invalidateAfterEdit} />
        </div>
      )}
      {detailQuery.data && (
        <div className="mr-tooltip-pipeline">
          <span className="mr-tooltip-pipeline-label jira-tooltip-field-label">Versions</span>
          <JiraFixVersionsField
            ticketKey={ticket.ticketKey}
            repoName={repoName}
            canEdit={detailQuery.data.canEditFixVersions}
            fixVersions={detailQuery.data.fixVersions}
            onMutated={invalidateAfterEdit}
          />
        </div>
      )}
      {/* Une subtask (JiraTooltipTarget.branches toujours vide) n'est référencée par aucune
          branche en propre — seul son ticket parent l'est. */}
      {ticket.branches.length > 0 && (
        <div className="jira-branch-pills">
          {ticket.branches.map((b) => (
            <button
              key={b}
              type="button"
              className="jira-branch-pill"
              title={`Aller à la branche ${b} dans le graphe`}
              onClick={() => onFlyToBranch(b)}
            >
              {b}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
